/**
 * The edit form of a record that is a name and an optional description — a
 * role, a group.
 *
 * The form holds strings, because a text field does. The record holds `null`
 * for "no description", because an empty string is a description that renders
 * as a blank line. These convert between the two.
 */

export interface NamedForm {
  name: string;
  description: string;
}

export interface NamedRecord {
  name: string;
  description: string | null;
}

/** What a PATCH of a named record takes. Absent is untouched; `null` clears. */
export interface NamedChanges {
  name?: string;
  description?: string | null;
}

export const BLANK_NAMED_FORM: NamedForm = { name: '', description: '' };

export function namedFormFor(record: NamedRecord): NamedForm {
  return { name: record.name, description: record.description ?? '' };
}

/**
 * What the form changed, and nothing else.
 *
 * Both sides of every field sent go in the audit row, and a rename is the row
 * that explains why the same record reads under two names either side of it.
 * Posting an untouched name back would bury that among no-op diffs. An empty
 * result means nothing changed — send nothing; the server refuses an empty
 * PATCH rather than auditing it.
 */
export function namedChanges(original: NamedRecord, next: NamedForm): NamedChanges {
  const before = namedFormFor(original);
  const changes: NamedChanges = {};

  if (next.name.trim() !== before.name) changes.name = next.name.trim();
  if (next.description.trim() !== before.description) {
    changes.description = next.description.trim() || null;
  }

  return changes;
}
